import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import { useToast, ToastType, Spinner } from "@cynaps/ui";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceArea,
} from "recharts";
import { ExpertiseSection } from "./components";
import "./EarningsDashboard.css";

interface TrustLevel {
  level: string;
  multiplier: number;
  tasks_completed: number;
  accuracy_score: number;
  honeypot_pass_rate: number;
  total_honeypots: number;
  passed_honeypots: number;
  fraud_flags: number;
  is_suspended: boolean;
}

interface Transaction {
  id: number;
  type: string;
  stage: string | null;
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
}

interface EarningsSummary {
  total_tasks: number;
  total_earned: number;
  pending_approval: number;
  available_balance: number;
  total_withdrawn: number;
  weekly_earnings: number;
  monthly_earnings: number;
  trust_level: TrustLevel;
  recent_transactions: Transaction[];
  daily_earnings?: { date: string; amount: number }[];
  activity_streak?: number;
  activity_days?: string[];
}

const LEVEL_CONFIG: Record<
  string,
  {
    color: string;
    icon: string;
    next: string | null;
    tasksReq: number;
    accuracyReq: number;
  }
> = {
  new: {
    color: "#6b7280",
    icon: "◇",
    next: "junior",
    tasksReq: 0,
    accuracyReq: 0,
  },
  junior: {
    color: "#22c55e",
    icon: "◆",
    next: "regular",
    tasksReq: 50,
    accuracyReq: 70,
  },
  regular: {
    color: "#3b82f6",
    icon: "★",
    next: "senior",
    tasksReq: 200,
    accuracyReq: 80,
  },
  senior: {
    color: "#a855f7",
    icon: "✦",
    next: "expert",
    tasksReq: 500,
    accuracyReq: 90,
  },
  expert: {
    color: "#f59e0b",
    icon: "✧",
    next: null,
    tasksReq: 1000,
    accuracyReq: 95,
  },
};

const LEVEL_ORDER = ["new", "junior", "regular", "senior", "expert"];

// Floating pixels decoration - matching landing page
const FloatingPixels = () => {
  const pixels = [
    { x: "5%", y: "15%", size: 3, opacity: 0.2 },
    { x: "12%", y: "45%", size: 4, opacity: 0.3 },
    { x: "20%", y: "70%", size: 3, opacity: 0.15 },
    { x: "85%", y: "20%", size: 4, opacity: 0.25 },
    { x: "90%", y: "55%", size: 3, opacity: 0.2 },
    { x: "75%", y: "80%", size: 4, opacity: 0.3 },
  ];

  return (
    <div className="floating-pixels">
      {pixels.map((p, i) => (
        <motion.div
          key={i}
          className="pixel"
          style={{
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            opacity: p.opacity,
          }}
          animate={{
            opacity: [p.opacity * 0.5, p.opacity, p.opacity * 0.5],
            scale: [0.9, 1, 0.9],
          }}
          transition={{
            duration: 4 + Math.random() * 2,
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
          }}
        />
      ))}
    </div>
  );
};

/**
 * Formats a number for display as Credits, without the "cr" suffix.
 */
const formatCreditsNum = (amount: number): string => {
  const num = Math.abs(amount);
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }
  return `${Math.round(num)}`;
};

/**
 * Format a number as Credits (platform currency).
 * No currency symbols — just plain number + "Credits".
 */
const GoldCoinIcon = ({ size = 28 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" style={{ filter: "drop-shadow(0 4px 6px rgba(234, 179, 8, 0.25))", verticalAlign: "middle", marginLeft: "4px" }}>
    <defs>
      <linearGradient id="coinGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FDE047" />
        <stop offset="50%" stopColor="#EAB308" />
        <stop offset="100%" stopColor="#A16207" />
      </linearGradient>
      <linearGradient id="coinInner" x1="1" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#FEF08A" />
        <stop offset="100%" stopColor="#CA8A04" />
      </linearGradient>
    </defs>
    <circle cx="16" cy="16" r="14" fill="url(#coinGrad)" />
    <circle cx="16" cy="16" r="10.5" fill="url(#coinInner)" />
    <path d="M19 11.5 A 5.5 5.5 0 1 0 19 20.5" stroke="#FEF9C3" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    <line x1="16" y1="9" x2="16" y2="23" stroke="#FEF9C3" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const CreditsDisplay: React.FC<{ amount: number; prefix?: string }> = ({ amount, prefix = "" }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
    {prefix}
    {formatCreditsNum(amount)}
    <GoldCoinIcon size={24} />
  </span>
);

export const EarningsDashboard: React.FC = () => {
  const history = useHistory();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  // Chart Range & Zoom States (matching client-side features)
  const [chartRange, setChartRange] = useState<"7d" | "14d" | "30d" | "all" | "custom">("14d");
  const [chartStartDate, setChartStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().split("T")[0];
  });
  const [chartEndDate, setChartEndDate] = useState<string>(() => {
    return new Date().toISOString().split("T")[0];
  });
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);
  const [zoomedRange, setZoomedRange] = useState<{ start: string; end: string } | null>(null);

  const fetchEarnings = useCallback(async () => {
    try {
      const response = await fetch("/api/annotators/earnings", {
        credentials: "include",
      });

      if (response.status === 403) {
        history.push("/annotators/login");
        return;
      }

      const data = await response.json();

      // Ensure trust_level exists with defaults
      if (!data.trust_level) {
        data.trust_level = {
          level: "new",
          multiplier: 1,
          tasks_completed: 0,
          accuracy_score: 0,
          honeypot_pass_rate: 0,
          total_honeypots: 0,
          passed_honeypots: 0,
          fraud_flags: 0,
          is_suspended: false,
        };
      }

      // Generate mock daily earnings for the chart if not provided
      if (!data.daily_earnings) {
        const days = 30;
        data.daily_earnings = Array.from({ length: days }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (days - 1 - i));
          return {
            date: date.toISOString().split("T")[0],
            amount: Math.random() * 500 + 100,
          };
        });
      }

      // Generate mock activity days for streak calendar if not provided
      if (!data.activity_days) {
        const today = new Date();
        data.activity_days = [];
        data.activity_streak = 0;
        let streak = 0;
        for (let i = 0; i < 60; i++) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          if (Math.random() > 0.3) {
            data.activity_days.push(date.toISOString().split("T")[0]);
            if (i === streak) streak++;
          }
        }
        data.activity_streak = streak;
      }

      setSummary(data);
    } catch (error) {
      console.error("Failed to fetch earnings:", error);
      toast?.show({
        message: "Failed to load earnings data",
        type: ToastType.error,
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [history, toast]);

  useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate level progress
  const levelProgress = useMemo(() => {
    if (!summary)
      return {
        percentage: 0,
        nextLevel: null,
        tasksNeeded: 0,
        accuracyNeeded: 0,
      };

    const currentLevelIndex = LEVEL_ORDER.indexOf(summary.trust_level.level);
    const nextLevel = LEVEL_CONFIG[summary.trust_level.level].next;

    if (!nextLevel)
      return {
        percentage: 100,
        nextLevel: null,
        tasksNeeded: 0,
        accuracyNeeded: 0,
      };

    const nextConfig = LEVEL_CONFIG[nextLevel];
    const taskProgress = Math.min(100, (summary.trust_level.tasks_completed / nextConfig.tasksReq) * 100);
    const accuracyProgress = Math.min(100, (summary.trust_level.accuracy_score / nextConfig.accuracyReq) * 100);

    return {
      percentage: Math.min(taskProgress, accuracyProgress),
      nextLevel,
      tasksNeeded: Math.max(0, nextConfig.tasksReq - summary.trust_level.tasks_completed),
      accuracyNeeded: nextConfig.accuracyReq,
    };
  }, [summary]);

  // Generate calendar data
  const calendarData = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days: {
      date: Date;
      isActive: boolean;
      isToday: boolean;
      inMonth: boolean;
    }[] = [];

    for (let i = 0; i < startPadding; i++) {
      const date = new Date(year, month, -startPadding + i + 1);
      days.push({ date, isActive: false, isToday: false, inMonth: false });
    }

    const activitySet = new Set(summary?.activity_days || []);
    const today = new Date().toISOString().split("T")[0];

    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      const dateStr = date.toISOString().split("T")[0];
      days.push({
        date,
        isActive: activitySet.has(dateStr),
        isToday: dateStr === today,
        inMonth: true,
      });
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isActive: false, isToday: false, inMonth: false });
    }

    return days;
  }, [selectedMonth, summary?.activity_days]);

  // Raw Chart Data based on time range filter
  const rawChartData = useMemo(() => {
    if (!summary?.daily_earnings || summary.daily_earnings.length === 0) return [];

    let filtered = [...summary.daily_earnings];
    filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (chartRange === "7d") {
      filtered = filtered.slice(-7);
    } else if (chartRange === "14d") {
      filtered = filtered.slice(-14);
    } else if (chartRange === "30d") {
      filtered = filtered.slice(-30);
    } else if (chartRange === "custom") {
      filtered = filtered.filter((d) => d.date >= chartStartDate && d.date <= chartEndDate);
    }

    return filtered.map((item) => ({
      date: item.date,
      amount: item.amount || 0,
    }));
  }, [summary?.daily_earnings, chartRange, chartStartDate, chartEndDate]);

  // Active chart data considering drag-to-zoom
  const activeChartData = useMemo(() => {
    if (!zoomedRange) return rawChartData;
    const leftIndex = rawChartData.findIndex((d) => d.date === zoomedRange.start);
    const rightIndex = rawChartData.findIndex((d) => d.date === zoomedRange.end);
    if (leftIndex === -1 || rightIndex === -1) return rawChartData;
    const start = Math.min(leftIndex, rightIndex);
    const end = Math.max(leftIndex, rightIndex);
    return rawChartData.slice(start, end + 1);
  }, [rawChartData, zoomedRange]);

  // Dynamic Chart Statistics
  const chartStats = useMemo(() => {
    if (!activeChartData.length) return { total: 0, avg: 0, peak: 0 };
    const total = activeChartData.reduce((sum, d) => sum + d.amount, 0);
    const avg = total / activeChartData.length;
    const peak = Math.max(...activeChartData.map((d) => d.amount), 0);
    return { total, avg, peak };
  }, [activeChartData]);

  const handleZoom = () => {
    if (!refAreaLeft || !refAreaRight || refAreaLeft === refAreaRight) {
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }
    setZoomedRange({ start: refAreaLeft, end: refAreaRight });
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  const handleZoomOut = () => {
    setZoomedRange(null);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  if (loading) {
    return (
      <div className="earnings-page">
        <div className="earnings-loading">
          <Spinner size={64} />
          <span className="loader-text">Loading earnings data...</span>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="earnings-page">
        <div className="earnings-error">
          <span className="error-code">ERROR_404</span>
          <h2>Unable to load earnings</h2>
          <p className="error-desc">Something went wrong while fetching your data.</p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="retry-btn"
            onClick={fetchEarnings}
          >
            Try Again
          </motion.button>
        </div>
      </div>
    );
  }

  const levelConfig = LEVEL_CONFIG[summary.trust_level?.level || "new"] || LEVEL_CONFIG.new;
  const currentLevelIndex = LEVEL_ORDER.indexOf(summary.trust_level?.level || "new");

  return (
    <div className="earnings-page">
      <FloatingPixels />

      {/* Background grid pattern - matching landing */}
      <div className="bg-grid" />

      {/* Gradient glow */}
      <div className="bg-glow" />

      <div className="earnings-container">
        {/* Header Section */}
        <motion.header
          className="earnings-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="header-left">
            <span className="header-tag">// DASHBOARD</span>
            <h1 className="header-title">Earnings</h1>
            <p className="header-subtitle">Track your performance and revenue</p>
          </div>
          <motion.button
            whileHover={{
              scale: 1.02,
              boxShadow: "0 0 30px rgba(139, 92, 246, 0.3)",
            }}
            whileTap={{ scale: 0.98 }}
            className="payout-btn"
            onClick={() => history.push("/annotators/payouts")}
          >
            Request Payout →
          </motion.button>
        </motion.header>

        {/* Stats Row - Lambda style */}
        <motion.div
          className="stats-row bg-black"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="stat-card available">
            <div className="stat-label">AVAILABLE BALANCE</div>
            <div className="stat-value primary">
              <CreditsDisplay amount={summary.available_balance} />
            </div>
            <div className="stat-hint">Ready to use</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">PENDING</div>
            <div className="stat-value">
              <CreditsDisplay amount={summary.pending_approval} />
            </div>
            <div className="stat-hint">Awaiting approval</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">TOTAL EARNED</div>
            <div className="stat-value">
              <CreditsDisplay amount={summary.total_earned} />
            </div>
            <div className="stat-hint">Lifetime credits</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">TASKS COMPLETED</div>
            <div className="stat-value">{summary.total_tasks.toLocaleString()}</div>
            <div className="stat-hint">All time</div>
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="main-grid">
          {/* Left Column */}
          <div className="left-col">
            {/* Trust Level Section */}
            <motion.section
              className="section-card trust-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="section-header">
                <span className="section-number">01/</span>
                <h2 className="section-title">Trust Level</h2>
              </div>

              <div className="trust-content">
                <div className="trust-main">
                  <div className="level-display">
                    <span className="level-icon" style={{ color: levelConfig.color }}>
                      {levelConfig.icon}
                    </span>
                    <div className="level-info">
                      <span className="level-name">{summary.trust_level.level.toUpperCase()}</span>
                      <span className="level-multiplier">{summary.trust_level.multiplier}x earnings multiplier</span>
                    </div>
                  </div>

                  {levelProgress.nextLevel && (
                    <div className="progress-section">
                      <div className="progress-meta">
                        <span>Progress to {levelProgress.nextLevel.toUpperCase()}</span>
                        <span>{Math.round(levelProgress.percentage)}%</span>
                      </div>
                      <div className="progress-track">
                        <motion.div
                          className="progress-fill"
                          initial={{ width: 0 }}
                          animate={{ width: `${levelProgress.percentage}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                          style={{ backgroundColor: levelConfig.color }}
                        />
                      </div>
                      <p className="progress-hint">
                        {levelProgress.tasksNeeded > 0 && `${levelProgress.tasksNeeded} more tasks needed`}
                        {levelProgress.tasksNeeded > 0 &&
                          summary.trust_level.accuracy_score < levelProgress.accuracyNeeded &&
                          " • "}
                        {summary.trust_level.accuracy_score < levelProgress.accuracyNeeded &&
                          `${levelProgress.accuracyNeeded}% accuracy required`}
                      </p>
                    </div>
                  )}
                </div>

                {/* Level progression visual */}
                <div className="level-progression">
                  {LEVEL_ORDER.map((level, index) => {
                    const config = LEVEL_CONFIG[level];
                    const isCurrent = index === currentLevelIndex;
                    const isPast = index < currentLevelIndex;
                    return (
                      <React.Fragment key={level}>
                        <div className={`level-node ${isCurrent ? "current" : ""} ${isPast ? "past" : ""}`}>
                          <span
                            className="node-icon"
                            style={{
                              color: isPast || isCurrent ? config.color : "#333",
                            }}
                          >
                            {config.icon}
                          </span>
                          <span className="node-label">{level}</span>
                        </div>
                        {index < LEVEL_ORDER.length - 1 && (
                          <div className={`level-connector ${isPast ? "filled" : ""}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Trust Stats Grid */}
              <div className="trust-stats">
                <div className="trust-stat">
                  <span className="stat-num">{summary.trust_level.tasks_completed}</span>
                  <span className="stat-lbl">Tasks</span>
                </div>
                <div className="trust-stat">
                  <span className="stat-num">{summary.trust_level.accuracy_score.toFixed(1)}%</span>
                  <span className="stat-lbl">Accuracy</span>
                </div>
                <div className="trust-stat">
                  <span className={`stat-num ${summary.trust_level.fraud_flags > 0 ? "warning" : ""}`}>
                    {summary.trust_level.fraud_flags}
                  </span>
                  <span className="stat-lbl">Flags</span>
                </div>
              </div>

              {summary.trust_level.fraud_flags > 0 && (
                <div className="fraud-warning">
                  <span className="warning-icon">!</span>
                  <span>
                    {summary.trust_level.fraud_flags} quality flag(s) detected. Maintain high accuracy to remove.
                  </span>
                </div>
              )}
            </motion.section>

            {/* Earnings Chart Section */}
            <motion.section
              className="section-card chart-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="section-header chart-header-row">
                <div className="header-left-title">
                  <span className="section-number">02/</span>
                  <h2 className="section-title">Earnings Trend</h2>
                </div>

                <div className="chart-controls">
                  <div className="range-pills-group">
                    {(["7d", "14d", "30d", "all", "custom"] as const).map((r) => (
                      <button
                        key={r}
                        type="button"
                        className={`range-pill ${chartRange === r ? "active" : ""}`}
                        onClick={() => {
                          setChartRange(r);
                          setZoomedRange(null);
                        }}
                      >
                        {r === "all" ? "All" : r === "custom" ? "Custom" : r.toUpperCase()}
                      </button>
                    ))}
                  </div>

                  {chartRange === "custom" && (
                    <div className="custom-date-inputs">
                      <input
                        type="date"
                        value={chartStartDate}
                        onChange={(e) => {
                          setChartStartDate(e.target.value);
                          setZoomedRange(null);
                        }}
                        className="date-input"
                      />
                      <span className="date-sep">to</span>
                      <input
                        type="date"
                        value={chartEndDate}
                        onChange={(e) => {
                          setChartEndDate(e.target.value);
                          setZoomedRange(null);
                        }}
                        className="date-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="chart-wrapper relative-chart-container" style={{ height: "300px", marginTop: "20px" }}>
                {zoomedRange && (
                  <button
                    type="button"
                    onClick={handleZoomOut}
                    className="zoom-reset-btn"
                    title="Reset Zoom"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                    <span>Reset Zoom</span>
                  </button>
                )}

                {activeChartData.length === 0 ? (
                  <div className="chart-empty-state">
                    <span>No earnings data for this period</span>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={activeChartData}
                      margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                      onMouseDown={(e) => e?.activeLabel && setRefAreaLeft(e.activeLabel)}
                      onMouseMove={(e) => refAreaLeft && e?.activeLabel && setRefAreaRight(e.activeLabel)}
                      onMouseUp={handleZoom}
                    >
                      <defs>
                        <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.5} />
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(dateStr) => {
                          const d = new Date(dateStr);
                          return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                        }}
                        stroke="rgba(255, 255, 255, 0.4)"
                        tick={{ fill: "rgba(255, 255, 255, 0.4)", fontSize: 11, fontFamily: "monospace" }}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={10}
                        minTickGap={25}
                      />
                      <YAxis
                        stroke="rgba(255, 255, 255, 0.4)"
                        tick={{ fill: "rgba(255, 255, 255, 0.4)", fontSize: 11, fontFamily: "monospace" }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => formatCreditsNum(value)}
                        tickMargin={8}
                      />
                      <RechartsTooltip
                        cursor={{ stroke: "rgba(139, 92, 246, 0.4)", strokeWidth: 1, strokeDasharray: "4 4" }}
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const val = payload[0].value as number;
                            const d = new Date(label);
                            const dateStr = d.toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            });
                            return (
                              <div className="chart-glass-tooltip">
                                <div className="tooltip-date-header">{dateStr}</div>
                                <div className="tooltip-value-row">
                                  <span className="tooltip-metric-label">Earnings:</span>
                                  <CreditsDisplay amount={val} />
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        name="Earnings"
                        stroke="#8b5cf6"
                        strokeWidth={2.5}
                        fillOpacity={1}
                        fill="url(#colorAmount)"
                        activeDot={{ r: 5, fill: "#c084fc", stroke: "#1e1b4b", strokeWidth: 2 }}
                      />
                      {refAreaLeft && refAreaRight ? (
                        <ReferenceArea
                          x1={refAreaLeft}
                          x2={refAreaRight}
                          strokeOpacity={0.3}
                          fill="#8b5cf6"
                          fillOpacity={0.15}
                        />
                      ) : null}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Chart Stats */}
              <div className="chart-summary">
                <div className="summary-item">
                  <span className="summary-label">PERIOD TOTAL</span>
                  <span className="summary-value">
                    <CreditsDisplay amount={chartStats.total} />
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">DAILY AVG</span>
                  <span className="summary-value accent">
                    <CreditsDisplay amount={chartStats.avg} />
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">PEAK DAY</span>
                  <span className="summary-value">
                    <CreditsDisplay amount={chartStats.peak} />
                  </span>
                </div>
              </div>
            </motion.section>

            {/* Expertise & Badges - Full width below chart */}
            <ExpertiseSection />
          </div>

          {/* Right Column */}
          <div className="right-col">
            {/* Activity Calendar */}
            <motion.section
              className="section-card calendar-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              <div className="section-header">
                <span className="section-number">03/</span>
                <h2 className="section-title">Activity</h2>
                <div className="streak-display">
                  <span className="streak-num">{summary.activity_streak || 0}</span>
                  <span className="streak-text">day streak</span>
                </div>
              </div>
              
              <div className="calendar-stats" style={{ display: 'flex', gap: '24px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #1a1a1a', fontSize: '0.85rem', color: '#888' }}>
                <div>Total Active Days: <span style={{ color: '#fff', fontWeight: 600, fontSize: '1rem', marginLeft: '4px' }}>{summary.activity_days ? summary.activity_days.length : 0}</span></div>
                <div>Total Annotations: <span style={{ color: '#fff', fontWeight: 600, fontSize: '1rem', marginLeft: '4px' }}>{summary.total_tasks}</span></div>
              </div>

              <div className="calendar-nav">
                <button
                  className="nav-btn"
                  onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))}
                >
                  ←
                </button>
                <span className="nav-month">
                  {selectedMonth.toLocaleDateString("en-US", {
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <button
                  className="nav-btn"
                  onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))}
                >
                  →
                </button>
              </div>

              <div className="calendar">
                <div className="calendar-header">
                  {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                    <span key={i} className="cal-day-label">
                      {d}
                    </span>
                  ))}
                </div>
                <div className="calendar-body">
                  {calendarData.map((day: any, i: number) => (
                    <div
                      key={i}
                      className={`cal-day ${day.isActive ? "active" : ""} ${
                        day.isToday ? "today" : ""
                      } ${!day.inMonth ? "outside" : ""}`}
                    >
                      {day.date.getDate()}
                    </div>
                  ))}
                </div>
              </div>
            </motion.section>

            {/* Recent Transactions */}
            <motion.section
              className="section-card transactions-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <div className="section-header">
                <span className="section-number">04/</span>
                <h2 className="section-title">Recent Activity</h2>
              </div>

              <div className="transactions-list">
                {summary.recent_transactions.length === 0 ? (
                  <div className="empty-state">
                    <span className="empty-text">No transactions yet</span>
                    <span className="empty-hint">Complete tasks to start earning</span>
                  </div>
                ) : (
                  summary.recent_transactions.slice(0, 5).map((tx) => (
                    <div key={tx.id} className={`tx-row tx-type-${tx.type}`}>
                      <div className="tx-info">
                        <div className="tx-details">
                          <span className="tx-desc">{tx.description}</span>
                          <span className="tx-time">{formatDate(tx.created_at)}</span>
                        </div>
                      </div>
                      <div className="tx-amount-wrapper">
                        <span className={`tx-amount ${tx.amount >= 0 ? "positive" : "negative"}`}>
                          <CreditsDisplay amount={tx.amount} prefix={tx.amount >= 0 ? "+" : ""} />
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.section>

            {/* Payment Structure */}
            <motion.section
              className="section-card payment-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="section-header">
                <span className="section-number">05/</span>
                <h2 className="section-title">Payment Structure</h2>
              </div>

              <div className="payment-flow">
                <div className="payment-stage">
                  <span className="stage-percent">40%</span>
                  <span className="stage-label">Immediate</span>
                  <span className="stage-desc">On submission</span>
                </div>
                <div className="stage-arrow">→</div>
                <div className="payment-stage">
                  <span className="stage-percent">40%</span>
                  <span className="stage-label">Consensus</span>
                  <span className="stage-desc">Quality check</span>
                </div>
                <div className="stage-arrow">→</div>
                <div className="payment-stage">
                  <span className="stage-percent">20%</span>
                  <span className="stage-label">Review</span>
                  <span className="stage-desc">Expert approval</span>
                </div>
              </div>

              <div className="multiplier-note">
                Your <span className="highlight">{summary.trust_level.multiplier}x</span> multiplier applies to all
                stages
              </div>
            </motion.section>
          </div>
        </div>
      </div>
    </div>
  );
};
